import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TopNavbar from "../TopNavbar";
import Sidebar from "./Sidebar";
import useAuth from "../../../hooks/useAuth";
import { subscriptionAPI } from "../../../api/client";
import "./AppShell.css";

/*
 * The frame every module renders inside: app bar, sidebar, content, footer.
 * A module brings its navigation as data and its routes as children — see
 * modules/dms/plmNavigation.js and modules/manufacturing/manufacturingNavigation.js.
 *
 * `children` may be a function of { subStatus } for routes that need it.
 */

const MOBILE_QUERY = "(max-width: 900px)";
const SIDEBAR_ID = "app-sidebar";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

function readCollapsed(storageKey) {
  try {
    return localStorage.getItem(storageKey) === "1";
  } catch {
    return false;
  }
}

function ExpiredNotice({ isAdmin, onManage }) {
  return (
    <div className="app-shell-expired">
      <div className="app-shell-expired-card">
        <div style={{ fontSize: 40, marginBottom: 12 }} aria-hidden="true">🔒</div>
        <h2>Subscription Expired</h2>
        <p>
          {isAdmin
            ? "Your plan has expired. Renew now to restore access for your team."
            : "Your company's plan has expired. Please contact your administrator to renew."}
        </p>
        {isAdmin && (
          <button type="button" onClick={onManage}>
            Manage Subscription
          </button>
        )}
      </div>
    </div>
  );
}

export default function AppShell({
  storageKey,
  subtitle,
  homePath,
  sections,
  admin,
  badges,
  rightSlot,
  menuItems,
  children,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  // Two separate states: the desktop rail's collapsed/expanded preference,
  // which is remembered, and the phone drawer, which always starts closed.
  // Keeping them apart is what stops a desktop "expanded" turning into an
  // open overlay when the window narrows.
  const [railCollapsed, setRailCollapsed] = useState(() => readCollapsed(storageKey));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [subStatus, setSubStatus] = useState(null);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Crossing the breakpoint or changing page never leaves the drawer open.
  useEffect(() => {
    closeDrawer();
  }, [isMobile, location.pathname, closeDrawer]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeDrawer();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [drawerOpen, closeDrawer]);

  useEffect(() => {
    if (!user?.tenantId) return;
    subscriptionAPI
      .getStatus()
      .then((resp) => setSubStatus(resp.data))
      .catch(() => {});
  }, [user?.tenantId, location.pathname]);

  const toggleSidebar = () => {
    if (isMobile) {
      setDrawerOpen((open) => !open);
      return;
    }
    setRailCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(storageKey, next ? "1" : "0");
      } catch {
        // storage unavailable — keep in-memory state only
      }
      return next;
    });
  };

  const sidebarOpen = isMobile ? drawerOpen : !railCollapsed;
  const showExpired = subStatus?.isExpired && location.pathname !== "/subscription";

  return (
    <div className="app-shell">
      <TopNavbar
        subStatus={subStatus}
        sidebarOpen={sidebarOpen}
        sidebarId={SIDEBAR_ID}
        onToggleSidebar={toggleSidebar}
        subtitle={subtitle}
        homePath={homePath}
        rightSlot={rightSlot}
        menuItems={menuItems}
      />
      <div className="app-shell-body">
        <Sidebar
          id={SIDEBAR_ID}
          sections={sections}
          admin={admin}
          badges={badges}
          collapsed={!sidebarOpen}
          onToggle={toggleSidebar}
        />
        {isMobile && drawerOpen && (
          <div className="app-shell-backdrop" onClick={closeDrawer} aria-hidden="true" />
        )}
        <main className="app-shell-main">
          {showExpired ? (
            <ExpiredNotice isAdmin={user?.isAdmin} onManage={() => navigate("/subscription")} />
          ) : typeof children === "function" ? (
            children({ subStatus })
          ) : (
            children
          )}
        </main>
      </div>
      <footer className="app-shell-footer">
        © {new Date().getFullYear()} DecisionFlow. All rights reserved.
      </footer>
    </div>
  );
}
